import { z } from "zod";
import { postingKeyRefSchema } from "../accounting/posting-keys.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");
const ibanSchema = z
    .string()
    .transform((value) => value.replace(/\s+/g, "").toUpperCase())
    .pipe(z.string().regex(/^TR\d{24}$/, "IBAN TR ile başlayan 26 karakter olmalıdır"))
    .describe("TR IBAN (boşluksuz). Sağlama basamağı sunucuda doğrulanır; loglara maskeli yazılır");
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const reasonSchema = z.string().trim().min(3).max(500);
// ── Sabitler ve etiketler ────────────────────────────────────────────────────
export const CASH_REGISTER_KINDS = ["STATION", "SAFE", "HEAD_OFFICE"];
export const cashRegisterKindSchema = z.enum(CASH_REGISTER_KINDS);
export const CASH_REGISTER_KIND_LABELS = {
    STATION: "İstasyon kasası",
    SAFE: "Kasa dairesi / çelik kasa",
    HEAD_OFFICE: "Merkez kasa",
};
export const BANK_ACCOUNT_KINDS = ["CURRENT", "OVERDRAFT", "POS_BLOCKED", "DBS", "LOAN", "DEPOSIT"];
export const bankAccountKindSchema = z.enum(BANK_ACCOUNT_KINDS);
export const BANK_ACCOUNT_KIND_LABELS = {
    CURRENT: "Vadesiz",
    OVERDRAFT: "Kredili mevduat (KMH)",
    POS_BLOCKED: "POS blokeli",
    DBS: "DBS",
    LOAN: "Kredi",
    DEPOSIT: "Vadeli mevduat",
};
export const NEGATIVE_CASH_POLICIES = ["WARN", "BLOCK"];
export const negativeCashPolicySchema = z.enum(NEGATIVE_CASH_POLICIES);
export const NEGATIVE_CASH_POLICY_LABELS = {
    WARN: "Uyar, izin ver",
    BLOCK: "Engelle",
};
export const POS_CARD_TYPES = ["CREDIT", "DEBIT", "FOREIGN", "OTHER"];
export const posCardTypeSchema = z.enum(POS_CARD_TYPES);
export const POS_CARD_TYPE_LABELS = {
    CREDIT: "Kredi kartı",
    DEBIT: "Banka kartı",
    FOREIGN: "Yurt dışı kartı",
    OTHER: "Diğer",
};
export const TREASURY_TXN_TYPES = [
    "CASH_IN",
    "CASH_OUT",
    "BANK_IN",
    "BANK_OUT",
    "TRANSFER",
    "BANK_FEE",
    "INTEREST_INCOME",
    "INTEREST_EXPENSE",
    "COUNT_SHORTAGE",
    "COUNT_SURPLUS",
    "OPENING",
    "POS_SETTLEMENT",
    "LOAN_DISBURSEMENT",
    "LOAN_INSTALLMENT",
    "COUNTERPARTY",
    "INSTRUMENT",
    "INVOICE",
    "EXPENSE",
    "SHIFT",
];
export const treasuryTxnTypeSchema = z.enum(TREASURY_TXN_TYPES);
export const TREASURY_TXN_TYPE_LABELS = {
    CASH_IN: "Kasa tahsil",
    CASH_OUT: "Kasa tediye",
    BANK_IN: "Gelen havale / EFT",
    BANK_OUT: "Giden havale / EFT",
    TRANSFER: "Transfer / virman",
    BANK_FEE: "Banka masrafı / komisyon",
    INTEREST_INCOME: "Faiz geliri",
    INTEREST_EXPENSE: "Faiz gideri",
    COUNT_SHORTAGE: "Sayım açığı",
    COUNT_SURPLUS: "Sayım fazlası",
    OPENING: "Açılış bakiyesi",
    POS_SETTLEMENT: "POS yatışı",
    LOAN_DISBURSEMENT: "Kredi kullandırımı",
    LOAN_INSTALLMENT: "Kredi taksiti",
    COUNTERPARTY: "Cari tahsilat / ödeme",
    INSTRUMENT: "Çek / senet tahsili ya da ödemesi",
    INVOICE: "Peşin fatura tahsilatı",
    EXPENSE: "Gider ödemesi",
    SHIFT: "Vardiya nakit hareketi",
};
export const TREASURY_ACCOUNT_KINDS = ["CASH", "BANK"];
export const treasuryAccountKindSchema = z.enum(TREASURY_ACCOUNT_KINDS);
export const treasuryFlowSchema = z.enum(["IN", "OUT"]);
export const treasuryLedgerStatusSchema = z.enum(["POSTED", "QUEUED", "NONE", "EXTERNAL"]);
export const TREASURY_LEDGER_STATUS_LABELS = {
    POSTED: "Muhasebeleşti",
    QUEUED: "Eşleştirme bekliyor",
    NONE: "Fiş üretmez",
    EXTERNAL: "Kaynak belgenin fişinde",
};
export const CASH_COUNT_STATUSES = ["MATCHED", "PENDING_APPROVAL", "APPROVED", "REJECTED"];
export const cashCountStatusSchema = z.enum(CASH_COUNT_STATUSES);
export const CASH_COUNT_STATUS_LABELS = {
    MATCHED: "Fark yok",
    PENDING_APPROVAL: "Fark onayı bekliyor",
    APPROVED: "Fark onaylandı",
    REJECTED: "Reddedildi",
};
/** TRY banknot ve madeni paralar (kuruş). Sayım ekranının varsayılan satırları. */
export const TRY_DENOMINATIONS = [
    "20000",
    "10000",
    "5000",
    "2000",
    "1000",
    "500",
    "100",
    "50",
    "25",
    "10",
    "5",
    "1",
];
export const BANK_STATEMENT_LINE_STATUSES = ["UNMATCHED", "SUGGESTED", "MATCHED", "IGNORED"];
export const bankStatementLineStatusSchema = z.enum(BANK_STATEMENT_LINE_STATUSES);
export const BANK_STATEMENT_LINE_STATUS_LABELS = {
    UNMATCHED: "Eşleşmedi",
    SUGGESTED: "Öneri var",
    MATCHED: "Eşleşti",
    IGNORED: "Yok sayıldı",
};
export const BANK_MATCHING_ACTIONS = [
    "COUNTERPARTY_COLLECTION",
    "COUNTERPARTY_PAYMENT",
    "BANK_FEE",
    "INTEREST_INCOME",
    "INTEREST_EXPENSE",
    "POS_SETTLEMENT",
    "DBS_PAYMENT",
    "LOAN_INSTALLMENT",
    "IGNORE",
];
export const bankMatchingActionSchema = z.enum(BANK_MATCHING_ACTIONS);
/** Yalnızca öneri / onayda (kural eylemi değil): çek tahsili ve kendi çekimizin ödenmesi (çek/senet modülü). */
export const INSTRUMENT_MATCHING_ACTIONS = ["INSTRUMENT_COLLECTION", "INSTRUMENT_PAYMENT"];
export const BANK_MATCHING_ACTION_LABELS = {
    INSTRUMENT_COLLECTION: "Çek tahsili",
    INSTRUMENT_PAYMENT: "Kendi çekimizin ödenmesi",
    COUNTERPARTY_COLLECTION: "Cari tahsilat",
    COUNTERPARTY_PAYMENT: "Cari ödeme",
    BANK_FEE: "Banka masrafı",
    INTEREST_INCOME: "Faiz geliri",
    INTEREST_EXPENSE: "Faiz gideri",
    POS_SETTLEMENT: "POS yatışı",
    DBS_PAYMENT: "DBS ödemesi",
    LOAN_INSTALLMENT: "Kredi taksiti",
    IGNORE: "Yok say",
    LINK_EXISTING: "Kayıtlı harekete bağla",
};
export const POS_BATCH_STATUSES = ["PENDING", "SETTLED", "CANCELLED"];
export const posBatchStatusSchema = z.enum(POS_BATCH_STATUSES);
export const POS_BATCH_STATUS_LABELS = {
    PENDING: "Yatış bekliyor",
    SETTLED: "Yattı",
    CANCELLED: "İptal",
};
export const GUARANTEE_KINDS = ["LETTER", "MORTGAGE", "PLEDGE"];
export const guaranteeKindSchema = z.enum(GUARANTEE_KINDS);
export const GUARANTEE_KIND_LABELS = {
    LETTER: "Teminat mektubu",
    MORTGAGE: "İpotek",
    PLEDGE: "Rehin",
};
export const GUARANTEE_DIRECTIONS = ["GIVEN", "RECEIVED"];
export const guaranteeDirectionSchema = z.enum(GUARANTEE_DIRECTIONS);
export const GUARANTEE_DIRECTION_LABELS = {
    GIVEN: "Verilen",
    RECEIVED: "Alınan",
};
export const GUARANTEE_STATUSES = ["ACTIVE", "RETURNED", "CALLED"];
export const guaranteeStatusSchema = z.enum(GUARANTEE_STATUSES);
export const GUARANTEE_STATUS_LABELS = {
    ACTIVE: "Yürürlükte",
    RETURNED: "İade edildi",
    CALLED: "Nakde çevrildi",
};
export const dbsItemStatusSchema = z.enum(["PLANNED", "PAID", "CANCELLED"]);
export const DBS_ITEM_STATUS_LABELS = {
    PLANNED: "Planlandı",
    PAID: "Ödendi",
    CANCELLED: "İptal",
};
export const loanInstallmentStatusSchema = z.enum(["PLANNED", "PAID"]);
export const LOAN_INSTALLMENT_STATUS_LABELS = {
    PLANNED: "Planlandı",
    PAID: "Ödendi",
};
// ── Banka kataloğu ve ayarlar ────────────────────────────────────────────────
export const bankSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    shortName: z.string(),
    eftCode: z.string(),
    swiftCode: z.string().nullable(),
    isParticipation: z.boolean(),
});
export const bankListSchema = z.object({ items: z.array(bankSchema) });
export const companyQuerySchema = z.object({ companyId: uuidSchema });
export const treasurySettingsSchema = z.object({
    companyId: uuidSchema,
    negativeCashPolicy: negativeCashPolicySchema,
    guaranteeMemoPosting: z.boolean().describe("Teminat mektupları için nazım hesap (9xx) kaydı atılsın mı"),
    posMismatchTolerance: nonNegativeMoneySchema.describe("Beklenen/gerçekleşen komisyon farkı alarm eşiği"),
    reminderDays: z.array(z.number().int().min(0).max(365)).min(1).max(10),
    version: z.number().int(),
});
export const updateTreasurySettingsSchema = patchOf(z.object({
    negativeCashPolicy: negativeCashPolicySchema,
    guaranteeMemoPosting: z.boolean(),
    posMismatchTolerance: nonNegativeMoneySchema,
    reminderDays: z.array(z.number().int().min(0).max(365)).min(1).max(10),
}));
// ── Kasalar ──────────────────────────────────────────────────────────────────
export const createCashRegisterSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().default(null).describe("null = merkez kasa"),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, "Kod büyük harf, rakam ve . _ - içerebilir"),
    name: z.string().trim().min(1).max(120),
    kind: cashRegisterKindSchema,
    ledgerAccountId: uuidSchema
        .nullable()
        .default(null)
        .describe("Kasaya özel alt hesap (ör. 100.01). Boşsa genel CASH eşleştirmesi kullanılır"),
});
export const updateCashRegisterSchema = patchOf(createCashRegisterSchema.omit({ companyId: true }).extend({ isActive: z.boolean() })).extend({ version: versionSchema });
export const cashRegisterSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    code: z.string(),
    name: z.string(),
    kind: cashRegisterKindSchema,
    currency: z.literal("TRY"),
    ledgerAccountId: uuidSchema.nullable(),
    ledgerAccountCode: z.string().nullable().describe("Kayıtların gittiği hesap (özel ya da genel eşleştirme)"),
    balance: moneySchema,
    lastCountAt: timestampSchema.nullable(),
    isActive: z.boolean(),
    version: z.number().int(),
});
export const cashRegisterListSchema = z.object({ items: z.array(cashRegisterSchema) });
export const listTreasuryDefinitionsQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    includeInactive: queryBoolean.optional(),
});
// ── Banka hesapları ──────────────────────────────────────────────────────────
export const createBankAccountSchema = z.object({
    companyId: uuidSchema,
    bankId: uuidSchema.nullable().default(null),
    bankName: z.string().trim().min(2).max(200).optional().describe("Katalogda olmayan banka için zorunlu"),
    name: z.string().trim().min(1).max(120),
    branchName: optionalText(120),
    branchCode: z
        .string()
        .trim()
        .regex(/^\d{1,6}$/, "Şube kodu en çok 6 haneli sayıdır")
        .nullable()
        .default(null),
    accountNo: optionalText(40),
    iban: ibanSchema,
    kind: bankAccountKindSchema,
    overdraftLimit: nonNegativeMoneySchema.default("0").describe("KMH limiti; yalnızca OVERDRAFT türünde"),
    ledgerAccountId: uuidSchema.nullable().default(null),
});
export const updateBankAccountSchema = patchOf(createBankAccountSchema.omit({ companyId: true }).extend({ isActive: z.boolean() })).extend({ version: versionSchema });
export const bankAccountSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    bankId: uuidSchema.nullable(),
    bankName: z.string(),
    name: z.string(),
    branchName: z.string().nullable(),
    branchCode: z.string().nullable(),
    accountNo: z.string().nullable(),
    iban: z.string(),
    kind: bankAccountKindSchema,
    currency: z.literal("TRY"),
    overdraftLimit: moneySchema,
    ledgerAccountId: uuidSchema.nullable(),
    ledgerAccountCode: z.string().nullable(),
    balance: moneySchema,
    availableBalance: moneySchema.describe("Bakiye + KMH limiti"),
    lastStatementDate: localDateSchema.nullable(),
    isActive: z.boolean(),
    version: z.number().int(),
});
export const bankAccountListSchema = z.object({ items: z.array(bankAccountSchema) });
// ── POS terminalleri ─────────────────────────────────────────────────────────
export const posCommissionRateInputSchema = z.object({
    cardType: posCardTypeSchema,
    installments: z.number().int().min(1).max(36).default(1).describe("1 = tek çekim"),
    rate: rateSchema.refine((value) => Number(value) >= 0 && Number(value) <= 100, "Oran 0–100 arasında olmalıdır"),
    validFrom: localDateSchema,
});
export const posCommissionRateSchema = z.object({
    id: uuidSchema,
    cardType: posCardTypeSchema,
    installments: z.number().int(),
    rate: z.string(),
    validFrom: localDateSchema,
    validTo: localDateSchema.nullable(),
});
export const createPosTerminalSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    bankId: uuidSchema.nullable().default(null),
    bankName: z.string().trim().min(2).max(200).optional(),
    name: z.string().trim().min(1).max(120),
    terminalNo: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{1,30}$/, "Terminal no harf, rakam ve - içerebilir"),
    merchantNo: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{1,30}$/, "Üye işyeri no harf, rakam ve - içerebilir")
        .nullable()
        .default(null),
    bankAccountId: uuidSchema.describe("Yatışın geleceği banka hesabı"),
    valueDays: z.number().int().min(0).max(120).default(1).describe("Blokeli gün sayısı (valör)"),
    ledgerAccountId: uuidSchema.nullable().default(null),
    commissionRates: z.array(posCommissionRateInputSchema).max(60).default([]),
});
export const updatePosTerminalSchema = patchOf(createPosTerminalSchema
    .omit({ companyId: true, stationId: true, commissionRates: true })
    .extend({ isActive: z.boolean() })).extend({ version: versionSchema });
export const posTerminalSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    bankId: uuidSchema.nullable(),
    bankName: z.string(),
    name: z.string(),
    terminalNo: z.string(),
    merchantNo: z.string().nullable(),
    bankAccountId: uuidSchema,
    bankAccountName: z.string(),
    valueDays: z.number().int(),
    ledgerAccountId: uuidSchema.nullable(),
    pendingAmount: moneySchema.describe("Yatışı beklenen brüt tutar"),
    commissionRates: z.array(posCommissionRateSchema),
    isActive: z.boolean(),
    version: z.number().int(),
});
export const posTerminalListSchema = z.object({ items: z.array(posTerminalSchema) });
/** Yeni oran eklenince aynı kart türü × taksit için açık uçlu önceki oran bir gün öncesinde kapatılır. */
export const addPosCommissionRatesSchema = z.object({
    rates: z.array(posCommissionRateInputSchema).min(1).max(60),
});
// ── Hazine hareketleri ───────────────────────────────────────────────────────
export const treasuryOffsetLineSchema = z.object({
    key: postingKeyRefSchema.describe("Karşı hesap anahtarı. Örn. EXPENSE, OTHER_INCOME, SALES_REVENUE, VAT_OUTPUT:VAT_STANDARD"),
    amount: positiveMoneySchema,
    stationId: uuidSchema.nullable().default(null),
    businessUnit: z.enum(["FUEL", "LPG", "MARKET", "WASH", "EV", "CAFE", "RENTAL"]).nullable().default(null),
    counterpartyId: uuidSchema.nullable().default(null),
    description: optionalText(200),
});
export const treasuryAccountRefSchema = z.object({ kind: treasuryAccountKindSchema, id: uuidSchema });
const txnBase = {
    companyId: uuidSchema,
    documentDate: localDateSchema,
    amount: positiveMoneySchema,
    description: z.string().trim().min(1).max(500),
    docNo: optionalText(60),
    stationId: uuidSchema.nullable().default(null),
};
const offsetLines = z
    .array(treasuryOffsetLineSchema)
    .min(1)
    .max(20)
    .describe("Karşı hesap satırları; toplamı belge tutarına eşit olmalıdır");
/**
 * Elle girilen hazine belgeleri. Cari tahsilat/ödeme buradan DEĞİL cari hareketinden girilir (kanal + kasa/banka seçimi);
 * sayım farkı, POS yatışı ve kredi belgeleri kendi uçlarından doğar.
 */
export const createTreasuryTransactionSchema = z.discriminatedUnion("txnType", [
    z.object({ ...txnBase, txnType: z.literal("CASH_IN"), cashRegisterId: uuidSchema, offsetLines }),
    z.object({ ...txnBase, txnType: z.literal("CASH_OUT"), cashRegisterId: uuidSchema, offsetLines }),
    z.object({ ...txnBase, txnType: z.literal("BANK_IN"), bankAccountId: uuidSchema, offsetLines }),
    z.object({ ...txnBase, txnType: z.literal("BANK_OUT"), bankAccountId: uuidSchema, offsetLines }),
    z.object({
        ...txnBase,
        txnType: z.literal("TRANSFER"),
        from: treasuryAccountRefSchema,
        to: treasuryAccountRefSchema,
    }),
    z.object({ ...txnBase, txnType: z.literal("BANK_FEE"), bankAccountId: uuidSchema }),
    z.object({ ...txnBase, txnType: z.literal("INTEREST_INCOME"), bankAccountId: uuidSchema }),
    z.object({ ...txnBase, txnType: z.literal("INTEREST_EXPENSE"), bankAccountId: uuidSchema }),
    z.object({
        ...txnBase,
        txnType: z.literal("OPENING"),
        account: treasuryAccountRefSchema,
        flow: treasuryFlowSchema.default("IN").describe("OUT: eksi bakiyeli (KMH kullanan) hesabın devri"),
    }),
]);
export const treasuryMovementSchema = z.object({
    id: uuidSchema,
    accountKind: treasuryAccountKindSchema,
    accountId: uuidSchema,
    accountName: z.string(),
    flow: treasuryFlowSchema,
    amount: moneySchema,
    statementLineId: uuidSchema.nullable(),
});
export const treasuryTransactionSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    txnNo: z.string().describe("Boşluksuz belge numarası: KB2026000042"),
    txnType: treasuryTxnTypeSchema,
    documentDate: localDateSchema,
    amount: moneySchema,
    description: z.string(),
    docNo: z.string().nullable(),
    stationId: uuidSchema.nullable(),
    offsetLines: z.array(z.object({ key: z.string(), side: z.enum(["DEBIT", "CREDIT"]), amount: moneySchema }).loose()),
    movements: z.array(treasuryMovementSchema),
    originModule: z.string().nullable(),
    originDocType: z.string().nullable(),
    originDocId: uuidSchema.nullable(),
    status: z.enum(["POSTED", "CANCELLED"]),
    ledgerStatus: treasuryLedgerStatusSchema,
    journalEntryId: uuidSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const treasuryWarningSchema = z.enum(["NEGATIVE_CASH_BALANCE", "OVERDRAFT_LIMIT_EXCEEDED"]);
export const TREASURY_WARNING_LABELS = {
    NEGATIVE_CASH_BALANCE: "Kasa bakiyesi eksiye düştü",
    OVERDRAFT_LIMIT_EXCEEDED: "Banka hesabı KMH limitinin altına indi",
};
export const treasuryTransactionResultSchema = z.object({
    transaction: treasuryTransactionSchema,
    warnings: z.array(treasuryWarningSchema),
});
export const treasuryTransactionPageSchema = pageOf(treasuryTransactionSchema);
export const listTreasuryTransactionsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    cashRegisterId: uuidSchema.optional(),
    bankAccountId: uuidSchema.optional(),
    txnType: treasuryTxnTypeSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    includeCancelled: queryBoolean.optional(),
});
export const cancelTreasuryTransactionSchema = z.object({ reason: reasonSchema, version: versionSchema });
// ── Kasa sayımı ──────────────────────────────────────────────────────────────
export const cashDenominationSchema = z.object({
    value: positiveMoneySchema.describe("Küpür değeri (kuruş). Örn. 20000 = 200 ₺"),
    quantity: z.number().int().min(0).max(1_000_000),
});
export const createCashCountSchema = z.object({
    cashRegisterId: uuidSchema,
    businessDate: localDateSchema,
    denominations: z
        .array(cashDenominationSchema)
        .min(1)
        .max(40)
        .refine((rows) => new Set(rows.map((row) => row.value)).size === rows.length, "Aynı küpür iki kez girilemez"),
    note: optionalText(500),
});
export const cashCountSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    cashRegisterId: uuidSchema,
    cashRegisterName: z.string(),
    countedAt: timestampSchema,
    businessDate: localDateSchema,
    denominations: z.array(cashDenominationSchema),
    countedTotal: moneySchema,
    systemBalance: moneySchema,
    difference: moneySchema.describe("Sayılan − sistem: + fazla, − açık"),
    status: cashCountStatusSchema,
    note: z.string().nullable(),
    decisionNote: z.string().nullable(),
    decidedAt: timestampSchema.nullable(),
    differenceTxnId: uuidSchema.nullable(),
    version: z.number().int(),
});
export const cashCountPageSchema = pageOf(cashCountSchema);
export const listCashCountsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    cashRegisterId: uuidSchema.optional(),
    status: cashCountStatusSchema.optional(),
});
export const decideCashCountSchema = z.object({
    note: z.string().trim().min(3).max(500).describe("Fark açıklaması / ret gerekçesi"),
    version: versionSchema,
});
// ── Banka ekstresi ───────────────────────────────────────────────────────────
const columnRef = z
    .union([z.string().trim().min(1).max(80), z.number().int().min(1).max(200)])
    .describe("Başlık adı ya da 1'den başlayan sütun sırası");
export const statementTemplateConfigSchema = z
    .object({
    delimiter: z.enum([";", ",", "\t", "|"]).default(";").describe("Yalnızca CSV"),
    headerRow: z
        .number()
        .int()
        .min(0)
        .max(100)
        .default(1)
        .describe("Başlık satırının sırası; 0 = başlık yok"),
    dateFormat: z.enum(["DD.MM.YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"]).default("DD.MM.YYYY"),
    decimalSeparator: z.enum([",", "."]).default(","),
    columns: z.object({
        bookingDate: columnRef,
        valueDate: columnRef.optional(),
        description: columnRef,
        amount: columnRef.optional().describe("İşaretli tek tutar sütunu"),
        debit: columnRef.optional().describe("Çıkış (borç) sütunu"),
        credit: columnRef.optional().describe("Giriş (alacak) sütunu"),
        balance: columnRef.optional(),
        reference: columnRef.optional(),
    }),
})
    .refine((config) => config.columns.amount !== undefined ||
    (config.columns.debit !== undefined && config.columns.credit !== undefined), { path: ["columns", "amount"], message: "Tek tutar sütunu ya da borç + alacak sütunları verilmelidir" });
export const createStatementTemplateSchema = z.object({
    name: z.string().trim().min(1).max(120),
    bankId: uuidSchema.nullable().default(null),
    fileFormat: z.enum(["CSV", "XLSX"]),
    config: statementTemplateConfigSchema,
});
export const updateStatementTemplateSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    bankId: uuidSchema.nullable().optional(),
    config: statementTemplateConfigSchema.optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
export const statementTemplateSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    bankId: uuidSchema.nullable(),
    fileFormat: z.enum(["CSV", "XLSX"]),
    config: statementTemplateConfigSchema,
    isActive: z.boolean(),
    version: z.number().int(),
});
export const statementTemplateListSchema = z.object({ items: z.array(statementTemplateSchema) });
export const statementImportRequestSchema = z.object({
    bankAccountId: uuidSchema,
    fileId: uuidSchema.describe("`POST /files` ile yüklenmiş ekstre dosyası (purpose: IMPORT)"),
    fileFormat: z.enum(["CSV", "XLSX", "MT940"]),
    templateId: uuidSchema.optional().describe("CSV / XLSX için zorunlu"),
});
export const parsedBankStatementLineSchema = z.object({
    lineNo: z.number().int(),
    bookingDate: localDateSchema,
    valueDate: localDateSchema.nullable(),
    amount: moneySchema.describe("+ giriş, − çıkış"),
    balanceAfter: moneySchema.nullable(),
    description: z.string(),
    reference: z.string().nullable(),
    duplicate: z.boolean().describe("Bu hesaba daha önce aktarılmış"),
});
export const statementImportPreviewSchema = z.object({
    valid: z.boolean(),
    lineCount: z.number().int(),
    duplicateCount: z.number().int(),
    openingBalance: moneySchema.nullable(),
    closingBalance: moneySchema.nullable(),
    lines: z.array(parsedBankStatementLineSchema),
    errors: z.array(z.object({ rowNo: z.number().int(), message: z.string() })),
});
export const statementImportResultSchema = z.object({
    importId: uuidSchema,
    lineCount: z.number().int(),
    importedCount: z.number().int(),
    duplicateCount: z.number().int(),
    suggestedCount: z.number().int(),
    autoMatchedCount: z.number().int(),
});
export const statementSuggestionSchema = z.object({
    action: bankMatchingActionSchema.or(z.enum(["LINK_EXISTING", ...INSTRUMENT_MATCHING_ACTIONS])),
    score: z.number().int().min(0).max(100),
    reason: z.string().describe("Önerinin gerekçesi (Türkçe; kişisel veri içermez)"),
    ruleId: uuidSchema.nullable(),
    autoApprove: z.boolean(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    openItemId: uuidSchema.nullable(),
    posBatchIds: z.array(uuidSchema),
    dbsItemId: uuidSchema.nullable(),
    loanInstallmentId: uuidSchema.nullable(),
    movementId: uuidSchema.nullable(),
    instrumentIds: z.array(uuidSchema).default([]).describe("Çek tahsili / ödemesi önerisinde çek/senetler"),
});
export const bankStatementLineSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    bankAccountName: z.string(),
    importId: uuidSchema,
    lineNo: z.number().int(),
    bookingDate: localDateSchema,
    valueDate: localDateSchema.nullable(),
    amount: moneySchema,
    balanceAfter: moneySchema.nullable(),
    description: z.string(),
    reference: z.string().nullable(),
    status: bankStatementLineStatusSchema,
    suggestion: statementSuggestionSchema.nullable(),
    matchedKind: z.string().nullable(),
    matchedDocId: uuidSchema.nullable(),
    matchedAutomatically: z.boolean(),
    ignoreReason: z.string().nullable(),
});
export const bankStatementLinePageSchema = pageOf(bankStatementLineSchema);
export const listBankStatementLinesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    bankAccountId: uuidSchema.optional(),
    status: bankStatementLineStatusSchema.optional(),
    queue: queryBoolean.optional().describe("true: yalnızca eşleşmeyenler kuyruğu (UNMATCHED + SUGGESTED)"),
});
/** Satırı onaylama: seçilen eyleme göre ilgili kayıt oluşturulur ve satır MATCHED olur. */
export const confirmBankStatementLineSchema = z.discriminatedUnion("action", [
    z.object({
        action: z.literal("COUNTERPARTY_COLLECTION"),
        counterpartyId: uuidSchema,
        openItemIds: z.array(uuidSchema).max(50).default([]).describe("Boşsa otomatik FIFO"),
    }),
    z.object({
        action: z.literal("COUNTERPARTY_PAYMENT"),
        counterpartyId: uuidSchema,
        openItemIds: z.array(uuidSchema).max(50).default([]),
    }),
    z.object({ action: z.literal("BANK_FEE") }),
    z.object({ action: z.literal("INTEREST_INCOME") }),
    z.object({ action: z.literal("INTEREST_EXPENSE") }),
    z.object({ action: z.literal("POS_SETTLEMENT"), batchIds: z.array(uuidSchema).min(1).max(200) }),
    z.object({ action: z.literal("DBS_PAYMENT"), dbsItemId: uuidSchema }),
    z.object({ action: z.literal("LOAN_INSTALLMENT"), installmentId: uuidSchema }),
    z.object({ action: z.literal("LINK_EXISTING"), movementId: uuidSchema }),
    z.object({
        action: z.literal("INSTRUMENT_COLLECTION"),
        instrumentIds: z
            .array(uuidSchema)
            .min(1)
            .max(200)
            .describe("Bankada tahsildeki / teminattaki çekler; toplam − satır tutarı = tahsil masrafı"),
    }),
    z.object({
        action: z.literal("INSTRUMENT_PAYMENT"),
        instrumentIds: z.array(uuidSchema).min(1).max(200).describe("Bu hesaptan ödenen kendi çeklerimiz"),
    }),
]);
export const ignoreBankStatementLineSchema = z.object({ reason: reasonSchema });
const ruleBase = z.object({
    name: z.string().trim().min(1).max(120),
    priority: z.number().int().min(1).max(9999).default(100),
    bankAccountId: uuidSchema.nullable().default(null),
    flow: z.enum(["IN", "OUT", "ANY"]).default("ANY"),
    amountMin: nonNegativeMoneySchema.nullable().default(null),
    amountMax: nonNegativeMoneySchema.nullable().default(null),
    descriptionPattern: z
        .string()
        .trim()
        .max(300)
        .nullable()
        .default(null)
        .refine((value) => {
        if (value === null)
            return true;
        try {
            new RegExp(value, "iu");
            return true;
        }
        catch {
            return false;
        }
    }, "Geçerli bir düzenli ifade olmalıdır")
        .describe("Açıklamada aranan düzenli ifade (büyük/küçük harf duyarsız)"),
    counterpartyIban: ibanSchema.nullable().default(null),
    action: bankMatchingActionSchema,
    counterpartyId: uuidSchema.nullable().default(null),
    posTerminalId: uuidSchema.nullable().default(null),
    autoApprove: z.boolean().default(false).describe("Eşleşen satır kullanıcı onayı beklemeden kaydedilir"),
});
export const createMatchingRuleSchema = ruleBase.extend({ companyId: uuidSchema });
export const updateMatchingRuleSchema = patchOf(ruleBase.extend({ isActive: z.boolean() })).extend({
    version: versionSchema,
});
export const matchingRuleSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    name: z.string(),
    priority: z.number().int(),
    isActive: z.boolean(),
    bankAccountId: uuidSchema.nullable(),
    flow: z.enum(["IN", "OUT", "ANY"]),
    amountMin: moneySchema.nullable(),
    amountMax: moneySchema.nullable(),
    descriptionPattern: z.string().nullable(),
    counterpartyIban: z.string().nullable(),
    action: bankMatchingActionSchema,
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    posTerminalId: uuidSchema.nullable(),
    autoApprove: z.boolean(),
    version: z.number().int(),
});
export const matchingRuleListSchema = z.object({ items: z.array(matchingRuleSchema) });
// ── POS batch ve yatış mutabakatı ────────────────────────────────────────────
export const posBatchLineInputSchema = z.object({
    cardType: posCardTypeSchema,
    installments: z.number().int().min(1).max(36).default(1),
    grossAmount: positiveMoneySchema,
});
export const createPosBatchSchema = z
    .object({
    terminalId: uuidSchema,
    businessDate: localDateSchema,
    lines: z
        .array(posBatchLineInputSchema)
        .min(1)
        .max(40)
        .refine((rows) => new Set(rows.map((row) => `${row.cardType}:${row.installments}`)).size === rows.length, "Aynı kart türü ve taksit iki kez girilemez"),
    description: optionalText(500),
    ledgerMode: z
        .enum(["POST", "EXTERNAL"])
        .describe("POST: POS alacağı fişi burada kesilir (offsetLines zorunlu). EXTERNAL: fişi kaynak belge keser"),
    offsetLines: z.array(treasuryOffsetLineSchema).max(20).default([]),
})
    .refine((value) => value.ledgerMode !== "POST" || value.offsetLines.length > 0, {
    path: ["offsetLines"],
    message: "Fiş kesilecekse karşı hesap satırları girilmelidir",
});
export const posBatchSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    batchNo: z.string().describe("PB2026000007"),
    terminalId: uuidSchema,
    terminalName: z.string(),
    bankName: z.string(),
    stationId: uuidSchema,
    businessDate: localDateSchema,
    grossAmount: moneySchema,
    expectedCommission: moneySchema,
    expectedNet: moneySchema,
    valueDate: localDateSchema,
    lines: z.array(z.object({
        cardType: posCardTypeSchema,
        installments: z.number().int(),
        grossAmount: moneySchema,
        commissionRate: z.string(),
        commissionAmount: moneySchema,
    })),
    description: z.string().nullable(),
    ledgerMode: z.enum(["POST", "EXTERNAL"]),
    ledgerStatus: z.enum(["POSTED", "QUEUED", "EXTERNAL"]),
    originModule: z.string().nullable(),
    status: posBatchStatusSchema,
    settlementId: uuidSchema.nullable(),
    version: z.number().int(),
});
export const posBatchPageSchema = pageOf(posBatchSchema);
export const listPosBatchesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    terminalId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    status: posBatchStatusSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const cancelPosBatchSchema = z.object({ reason: reasonSchema, version: versionSchema });
export const createPosSettlementSchema = z.object({
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    settledOn: localDateSchema,
    netAmount: positiveMoneySchema.describe("Bankaya yatan net tutar"),
    batchIds: z.array(uuidSchema).min(1).max(200),
    statementLineId: uuidSchema.nullable().default(null),
});
export const posSettlementSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    settledOn: localDateSchema,
    grossTotal: moneySchema,
    netAmount: moneySchema,
    commissionAmount: moneySchema.describe("Gerçekleşen komisyon = brüt − net"),
    expectedCommission: moneySchema,
    difference: moneySchema.describe("Gerçekleşen − beklenen komisyon"),
    mismatch: z.boolean().describe("Fark şirket toleransını aşıyor (alarm olayı yazıldı)"),
    treasuryTxnId: uuidSchema,
    batchIds: z.array(uuidSchema),
});
export const dateRangeCompanyQuerySchema = z.object({
    companyId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
});
export const posValueCalendarSchema = z.object({
    days: z.array(z.object({
        valueDate: localDateSchema,
        batchCount: z.number().int(),
        grossAmount: moneySchema,
        expectedNet: moneySchema,
        overdue: z.boolean(),
        byBank: z.array(z.object({ bankName: z.string(), expectedNet: moneySchema })),
    })),
    totalExpectedNet: moneySchema,
    overdueExpectedNet: moneySchema,
});
export const posCommissionAnalysisSchema = z.object({
    rows: z.array(z.object({
        bankName: z.string(),
        settledGross: moneySchema,
        actualCommission: moneySchema,
        expectedCommission: moneySchema,
        actualRate: z.string().describe("Gerçekleşen komisyon oranı (%; 4 ondalık)"),
        expectedRate: z.string(),
        pendingGross: moneySchema,
    })),
});
// ── Teminat mektupları, ipotek, rehin ────────────────────────────────────────
const guaranteeBase = z.object({
    kind: guaranteeKindSchema.default("LETTER"),
    direction: guaranteeDirectionSchema,
    bankId: uuidSchema.nullable().default(null),
    bankName: z.string().trim().min(2).max(200).nullable().default(null),
    counterpartyId: uuidSchema.describe("Muhatap / lehtar (genellikle dağıtıcı)"),
    referenceNo: optionalText(60),
    amount: positiveMoneySchema,
    issueDate: localDateSchema,
    expiryDate: localDateSchema.nullable().default(null).describe("null = süresiz"),
    commissionRate: rateSchema.nullable().default(null).describe("Dönem başına komisyon (%)"),
    commissionPeriodMonths: z.number().int().min(1).max(12).nullable().default(null),
    nextCommissionDate: localDateSchema.nullable().default(null),
    description: optionalText(500),
    attachmentFileId: uuidSchema.nullable().default(null).describe("`POST /files` ile yüklenmiş belge"),
});
export const createGuaranteeSchema = guaranteeBase.extend({ companyId: uuidSchema });
export const updateGuaranteeSchema = patchOf(guaranteeBase.omit({ direction: true, amount: true, kind: true })).extend({ version: versionSchema });
export const guaranteeSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    kind: guaranteeKindSchema,
    direction: guaranteeDirectionSchema,
    bankId: uuidSchema.nullable(),
    bankName: z.string().nullable(),
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    referenceNo: z.string().nullable(),
    amount: moneySchema,
    issueDate: localDateSchema,
    expiryDate: localDateSchema.nullable(),
    daysToExpiry: z.number().int().nullable(),
    commissionRate: z.string().nullable(),
    commissionPeriodMonths: z.number().int().nullable(),
    nextCommissionDate: localDateSchema.nullable(),
    nextCommissionAmount: moneySchema.nullable(),
    status: guaranteeStatusSchema,
    statusDate: localDateSchema.nullable(),
    statusNote: z.string().nullable(),
    description: z.string().nullable(),
    attachmentName: z.string().nullable(),
    memoPosted: z.boolean(),
    version: z.number().int(),
});
export const guaranteePageSchema = pageOf(guaranteeSchema);
export const listGuaranteesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    direction: guaranteeDirectionSchema.optional(),
    status: guaranteeStatusSchema.optional(),
    counterpartyId: uuidSchema.optional(),
});
export const changeGuaranteeStatusSchema = z.object({
    status: z.enum(["RETURNED", "CALLED"]),
    statusDate: localDateSchema,
    note: optionalText(500),
    version: versionSchema,
});
export const advanceGuaranteeCommissionSchema = z.object({ version: versionSchema });
export const setGuaranteeRequirementSchema = z.object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    requiredAmount: nonNegativeMoneySchema.describe("Bayilik sözleşmesinin istediği teminat tutarı"),
    note: optionalText(500),
});
export const guaranteeSummarySchema = z.object({
    rows: z.array(z.object({
        counterpartyId: uuidSchema,
        counterpartyName: z.string(),
        givenActive: moneySchema,
        receivedActive: moneySchema,
        requiredAmount: moneySchema.nullable(),
        shortfall: moneySchema.nullable().describe("İstenen − verilen (yalnızca pozitifse); teminat açığı"),
        nearestExpiry: localDateSchema.nullable(),
    })),
});
// ── DBS ──────────────────────────────────────────────────────────────────────
export const createDbsAgreementSchema = z.object({
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    counterpartyId: uuidSchema.describe("Dağıtıcı cari"),
    limitAmount: nonNegativeMoneySchema,
    note: optionalText(500),
});
export const updateDbsAgreementSchema = z.object({
    limitAmount: nonNegativeMoneySchema.optional(),
    note: z.string().trim().max(500).nullable().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
export const dbsAgreementSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    bankAccountName: z.string(),
    bankName: z.string(),
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    limitAmount: moneySchema,
    usedAmount: moneySchema.describe("Planlanmış (ödenmemiş) kalemlerin toplamı"),
    availableAmount: moneySchema,
    usagePercent: z.number().int().describe("Limit doluluk yüzdesi (0–999)"),
    note: z.string().nullable(),
    isActive: z.boolean(),
    version: z.number().int(),
});
export const dbsAgreementListSchema = z.object({ items: z.array(dbsAgreementSchema) });
export const createDbsItemSchema = z.object({
    agreementId: uuidSchema,
    openItemId: uuidSchema.nullable().default(null).describe("Dağıtıcı faturasının açık kalemi (varsa)"),
    invoiceNo: optionalText(60),
    dueDate: localDateSchema,
    amount: positiveMoneySchema,
    allowOverLimit: z.boolean().default(false).describe("Limit aşılıyorsa yine de planla"),
});
export const dbsItemSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    agreementId: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    bankAccountId: uuidSchema,
    bankName: z.string(),
    openItemId: uuidSchema.nullable(),
    invoiceNo: z.string().nullable(),
    dueDate: localDateSchema,
    amount: moneySchema,
    status: dbsItemStatusSchema,
    paidOn: localDateSchema.nullable(),
    counterpartyTxnId: uuidSchema.nullable(),
    version: z.number().int(),
});
export const dbsItemPageSchema = pageOf(dbsItemSchema);
export const listDbsItemsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    agreementId: uuidSchema.optional(),
    status: dbsItemStatusSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const payDbsItemSchema = z.object({
    paidOn: localDateSchema,
    statementLineId: uuidSchema.nullable().default(null),
    version: versionSchema,
});
export const cancelDbsItemSchema = z.object({ reason: reasonSchema, version: versionSchema });
/** Akaryakıt siparişi "limit yetersiz" uyarısı için. */
export const dbsLimitQuerySchema = z.object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    amount: nonNegativeMoneySchema.optional().describe("Verilecek siparişin tutarı"),
});
export const dbsLimitSummarySchema = z.object({
    counterpartyId: uuidSchema,
    hasAgreement: z.boolean(),
    limitAmount: moneySchema,
    usedAmount: moneySchema,
    availableAmount: moneySchema,
    sufficient: z.boolean().nullable().describe("amount verilmediyse null"),
});
// ── Krediler ─────────────────────────────────────────────────────────────────
export const loanInstallmentInputSchema = z
    .object({
    installmentNo: z.number().int().min(1).max(600),
    dueDate: localDateSchema,
    principalAmount: nonNegativeMoneySchema,
    interestAmount: nonNegativeMoneySchema,
    otherAmount: nonNegativeMoneySchema.default("0").describe("BSMV, KKDF, masraf"),
})
    .refine((row) => BigInt(row.principalAmount) + BigInt(row.interestAmount) + BigInt(row.otherAmount) > 0n, "Taksit tutarı sıfır olamaz");
const installmentsInput = z
    .array(loanInstallmentInputSchema)
    .max(600)
    .refine((rows) => new Set(rows.map((row) => row.installmentNo)).size === rows.length, "Taksit numaraları benzersiz olmalıdır");
export const createLoanSchema = z.object({
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    name: z.string().trim().min(1).max(120),
    referenceNo: optionalText(60),
    principal: positiveMoneySchema,
    annualRate: rateSchema.nullable().default(null),
    startDate: localDateSchema,
    note: optionalText(500),
    recordDisbursement: z
        .boolean()
        .default(true)
        .describe("true: kullandırım banka hesabına giriş olarak kaydedilir (BANK borç / BANK_LOAN alacak)"),
    installments: installmentsInput.default([]),
});
export const loanInstallmentSchema = z.object({
    id: uuidSchema,
    installmentNo: z.number().int(),
    dueDate: localDateSchema,
    principalAmount: moneySchema,
    interestAmount: moneySchema,
    otherAmount: moneySchema,
    totalAmount: moneySchema,
    status: loanInstallmentStatusSchema,
    paidOn: localDateSchema.nullable(),
    treasuryTxnId: uuidSchema.nullable(),
});
export const loanSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    bankAccountName: z.string(),
    bankName: z.string(),
    name: z.string(),
    referenceNo: z.string().nullable(),
    principal: moneySchema,
    annualRate: z.string().nullable(),
    startDate: localDateSchema,
    status: z.enum(["ACTIVE", "CLOSED"]),
    remainingPrincipal: moneySchema,
    nextDueDate: localDateSchema.nullable(),
    nextDueAmount: moneySchema.nullable(),
    note: z.string().nullable(),
    installments: z.array(loanInstallmentSchema),
    version: z.number().int(),
});
export const loanListSchema = z.object({ items: z.array(loanSchema) });
export const replaceLoanInstallmentsSchema = z.object({
    installments: installmentsInput.min(1),
    version: versionSchema,
});
export const importLoanInstallmentsSchema = z.object({
    fileId: uuidSchema.describe("Excel/CSV: Taksit No; Vade; Anapara; Faiz; Diğer"),
    version: versionSchema,
});
export const payLoanInstallmentSchema = z.object({
    paidOn: localDateSchema,
    statementLineId: uuidSchema.nullable().default(null),
});
// ── Raporlar ─────────────────────────────────────────────────────────────────
export const cashBookQuerySchema = z.object({
    cashRegisterId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
});
export const cashBookSchema = z.object({
    cashRegisterId: uuidSchema,
    cashRegisterName: z.string(),
    from: localDateSchema,
    to: localDateSchema,
    openingBalance: moneySchema.describe("Dönem başı devir"),
    totalIn: moneySchema,
    totalOut: moneySchema,
    closingBalance: moneySchema,
    days: z.array(z.object({
        date: localDateSchema,
        openingBalance: moneySchema,
        totalIn: moneySchema,
        totalOut: moneySchema,
        closingBalance: moneySchema,
    })),
    lines: z.array(z.object({
        movementId: uuidSchema,
        transactionId: uuidSchema,
        txnNo: z.string(),
        txnType: treasuryTxnTypeSchema,
        date: localDateSchema,
        description: z.string(),
        flow: treasuryFlowSchema,
        amount: moneySchema,
        balance: moneySchema.describe("Yürüyen bakiye"),
    })),
});
export const bankReconciliationQuerySchema = z.object({ bankAccountId: uuidSchema, asOf: localDateSchema });
export const bankReconciliationSchema = z.object({
    bankAccountId: uuidSchema,
    bankAccountName: z.string(),
    asOf: localDateSchema,
    statementBalance: moneySchema.nullable().describe("Ekstredeki son bakiye; ekstre yoksa null"),
    statementBalanceDate: localDateSchema.nullable(),
    bookBalance: moneySchema.describe("Defter (alt defter) bakiyesi"),
    difference: moneySchema.nullable().describe("Ekstre − defter"),
    unmatchedStatementTotal: moneySchema.describe("Ekstrede olup deftere işlenmemiş (eşleşmemiş) satırlar"),
    unlinkedBookTotal: moneySchema.describe("Defterde olup ekstrede görülmemiş hareketler"),
    unexplained: moneySchema.nullable().describe("Fark − açıklayan kalemler; 0 ise mutabık"),
    unmatchedBankStatementLines: z.array(z.object({ id: uuidSchema, bookingDate: localDateSchema, amount: moneySchema, description: z.string() })),
    unlinkedBookMovements: z.array(z.object({
        movementId: uuidSchema,
        transactionId: uuidSchema,
        txnNo: z.string(),
        date: localDateSchema,
        amount: moneySchema.describe("+ giriş, − çıkış"),
        description: z.string(),
    })),
});
export const cashPositionQuerySchema = z.object({
    companyId: uuidSchema.optional().describe("Verilmezse görülebilen tüm şirketler"),
    stationId: uuidSchema.optional(),
    asOf: localDateSchema.optional().describe("Varsayılan: bugün (Europe/Istanbul)"),
});
const positionTotals = z.object({
    cash: moneySchema,
    bank: moneySchema,
    bankAvailable: moneySchema.describe("Banka bakiyesi + kullanılabilir KMH"),
    posPending: moneySchema.describe("POS'ta bekleyen bloke net tutar"),
    posDueToday: moneySchema,
    chequesDueToday: moneySchema.describe("Bugün vadesi gelen alınan çekler (çek modülü yoksa 0)"),
    chequesPayableToday: moneySchema,
    dbsDueToday: moneySchema,
    loanDueToday: moneySchema,
    netToday: moneySchema.describe("Kasa + banka + bugün beklenen girişler − bugünkü çıkışlar"),
});
export const cashPositionSchema = z.object({
    asOf: localDateSchema,
    totals: positionTotals,
    companies: z.array(z.object({
        companyId: uuidSchema,
        companyName: z.string(),
        totals: positionTotals,
        cashRegisters: z.array(z.object({
            id: uuidSchema,
            name: z.string(),
            stationId: uuidSchema.nullable(),
            stationName: z.string().nullable(),
            balance: moneySchema,
        })),
        bankAccounts: z.array(z.object({
            id: uuidSchema,
            name: z.string(),
            bankName: z.string(),
            kind: bankAccountKindSchema,
            balance: moneySchema,
            overdraftLimit: moneySchema,
            availableBalance: moneySchema,
        })),
        posPending: z.array(z.object({
            terminalId: uuidSchema,
            terminalName: z.string(),
            stationId: uuidSchema,
            stationName: z.string(),
            pendingNet: moneySchema,
            dueToday: moneySchema,
            overdue: moneySchema,
        })),
        stations: z.array(z.object({
            stationId: uuidSchema.nullable().describe("null = merkez"),
            stationName: z.string(),
            cash: moneySchema,
            posPending: moneySchema,
        })),
    })),
});
/** Hazine alt defteri ↔ ana defter: aynı hesaba bağlı kasa/banka hareket bakiyesi ile hesabın defter bakiyesi. */
export const treasuryLedgerReconciliationSchema = z.object({
    companyId: uuidSchema,
    balanced: z.boolean(),
    rows: z.array(z.object({
        accountId: uuidSchema.nullable(),
        accountCode: z.string().nullable().describe("null: eşleştirme yok"),
        accountName: z.string().nullable(),
        members: z.array(z.string()).describe("Bu hesaba bağlı kasa / banka hesapları"),
        subledgerBalance: moneySchema,
        ledgerBalance: moneySchema,
        queuedAmount: moneySchema.describe("Eşleştirme bekleyen (fişi oluşmamış) hareketlerin net tutarı"),
        difference: moneySchema,
    })),
});
export const resuggestResultSchema = z.object({
    suggestedCount: z.number().int(),
    autoMatchedCount: z.number().int(),
});
//# sourceMappingURL=treasury.js.map